import {
  Outlet,
  createRootRouteWithContext,
  redirect,
  useRouterState,
} from "@tanstack/react-router";
import { TanStackRouterDevtoolsPanel } from "@tanstack/react-router-devtools";
import { TanStackDevtools } from "@tanstack/react-devtools";
import { useEffect } from "react";
import TanStackQueryDevtools from "../integrations/tanstack-query/devtools";
import type { QueryClient } from "@tanstack/react-query";
import {
  // canAccess,
  evaluatePermGuards,
} from "@/lib/perms";
import { userInfoQueryOptions } from "@/lib/queries/auth";
import { Toaster } from "@/components/ui/sonner";

interface MyRouterContext {
  queryClient: QueryClient;
}

const isDev = import.meta.env.VITE_ENVIRONMENT !== "production";

export const Route = createRootRouteWithContext<MyRouterContext>()({
  component: RootComponent,
  async beforeLoad(ctx) {
    const userInfoPromise =
      ctx.context.queryClient.ensureQueryData(userInfoQueryOptions);
    const permGuards = ctx.matches
      .flatMap((m) => m.staticData.permissionGuards)
      .filter(Boolean);
    let userInfo;
    try {
      userInfo = await userInfoPromise;
    } catch {
      return;
    }

    if (!evaluatePermGuards(userInfo, permGuards, ctx.location.pathname))
      throw redirect({ to: "/" });
  },
  head({ matches }) {
    const foundMatch = matches.findLast((m) => m.staticData.title);
    let title = "Governance Dashboard";
    if (foundMatch?.staticData.title) {
      title = `${foundMatch.staticData.title} - ` + title;
    }
    return {
      meta: [
        {
          title,
        },
      ],
    };
  },
});

function RootComponent() {
  const problematicScroll = useRouterState({
    select: (s) => s.matches.some((m) => m.staticData.isProblematicScrollPage),
  });

  useEffect(() => {
    if (problematicScroll) document.body.dataset.problematicScroll = "";
    else delete document.body.dataset.problematicScroll;

    return () => void delete document.body.dataset.problematicScroll;
  }, [problematicScroll]);

  return (
    <>
      <Outlet />

      <Toaster />

      <TanStackDevtools
        config={{
          position: "bottom-left",
          hideUntilHover: !isDev,
          requireUrlFlag: !isDev,
          urlFlag: "devtools",
          openHotkey: ["Control", "A"],
        }}
        eventBusConfig={
          import.meta.env.DEV
            ? { port: 3001, debug: true, connectToServerBus: true }
            : undefined
        }
        plugins={[
          { name: "Tanstack Router", render: <TanStackRouterDevtoolsPanel /> },
          TanStackQueryDevtools,
        ]}
      />
    </>
  );
}
